/**
 * Secure notes & incident log. Timestamped, searchable, export to PDF.
 */
import { Platform } from 'react-native';
import type { CareNote } from '../types/care';

const COLLECTION = 'care';
const NOTES_DOC = 'notes';

function getDb(): Promise<any> {
  if (Platform.OS === 'web') {
    return import('./unifiedAuthService').then((m) => m.getWebDb());
  }
  const { initializeFirebase, getMobileFirestore } = require('./unifiedAuthService');
  if (!initializeFirebase()) return Promise.resolve(null);
  const getFs = getMobileFirestore();
  return Promise.resolve(getFs ? getFs() : null);
}

function notesPath(recipientId: string): string {
  return `users/${recipientId}/${COLLECTION}/${NOTES_DOC}`;
}

export const careNotesService = {
  async list(recipientId: string): Promise<CareNote[]> {
    const db = await getDb();
    if (!db) return [];
    try {
      if (Platform.OS === 'web') {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, notesPath(recipientId)));
        const data = snap.exists() ? snap.data() : null;
        const list = (data?.items ?? []) as CareNote[];
        return list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      }
      const ref = db.collection('users').doc(recipientId).collection(COLLECTION).doc(NOTES_DOC);
      const snap = await ref.get();
      const data = snap.exists ? snap.data() : null;
      const list = (data?.items ?? []) as CareNote[];
      return list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    } catch (e) {
      console.warn('careNotesService.list error:', e);
      return [];
    }
  },

  search(notes: CareNote[], query: string): CareNote[] {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => n.body.toLowerCase().includes(q) || (n.type && n.type.toLowerCase().includes(q)));
  },

  async add(recipientId: string, note: Omit<CareNote, 'id' | 'createdAt'>): Promise<CareNote> {
    const list = await this.list(recipientId);
    const now = new Date().toISOString();
    const newNote: CareNote = {
      ...note,
      id: `note_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      createdAt: now,
    };
    list.unshift(newNote);
    await this.saveAll(recipientId, list);
    return newNote;
  },

  async delete(recipientId: string, noteId: string): Promise<void> {
    const list = (await this.list(recipientId)).filter((n) => n.id !== noteId);
    await this.saveAll(recipientId, list);
  },

  async saveAll(recipientId: string, items: CareNote[]): Promise<void> {
    const db = await getDb();
    if (!db) return;
    const payload = { items, updatedAt: new Date().toISOString() };
    if (Platform.OS === 'web') {
      const { doc, setDoc } = await import('firebase/firestore');
      await setDoc(doc(db, notesPath(recipientId)), payload, { merge: true });
    } else {
      await db.collection('users').doc(recipientId).collection(COLLECTION).doc(NOTES_DOC).set(payload, { merge: true });
    }
  },

  /** Export notes to PDF-ready content (plain text or HTML). Backend can generate PDF. */
  exportAsText(notes: CareNote[], recipientName: string): string {
    const lines = [`Care notes – ${recipientName}`, `Exported ${new Date().toISOString()}`, ''];
    notes.forEach((n) => {
      lines.push(`[${n.type ?? 'note'}] ${new Date(n.createdAt).toLocaleString()}`);
      lines.push(n.body);
      if (n.createdByName) lines.push(`— ${n.createdByName}`);
      lines.push('');
    });
    return lines.join('\n');
  },
};
